import _ from "lodash";
import { Box, Button, ButtonGroup, InputLabel, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import InputBox from "./InputBox";
import { useCallback, useState } from "react";
import ImageBox from "components/basic/ImageBox";
import FormContent from "../FormContent";
import { NestedField, PField, useFieldValue } from "./NestedField";
import { rotateImage, scaleImage } from "util/image";
import ConfirmDialog from "components/dialog/ConfirmDialog";
import { showDialog } from "components/dialog";
import GeolocationButton from "./GeolocationButton";
import { useCoordinates } from "util/geolocation";
import WindowDialog from "components/dialog/WindowDialog";
import useNotification from "notification"

/**
 * Converte um arquivo local para base64 usando cordova-plugin-file
 */
async function convertFileToBase64(filePath) {
    return new Promise((resolve, reject) => {
        if (!window.cordova) {
            reject(new Error("Cordova não disponível"));
            return;
        }
        
        window.resolveLocalFileSystemURL(filePath, 
            (fileEntry) => {
                fileEntry.file((file) => {
                    const reader = new FileReader();
                    reader.onloadend = function() {
                        // Remove o prefixo data:image/...;base64, se existir
                        const base64 = this.result.split(',')[1] || this.result;
                        resolve(base64);
                    };
                    reader.onerror = function(error) {
                        console.error("Erro ao ler arquivo:", error);
                        reject(error);
                    };
                    reader.readAsDataURL(file);
                }, reject);
            }, 
            (error) => {
                console.error("Erro ao resolver caminho do arquivo:", error);
                reject(error);
            }
        );
    });
}

/**
 * Input para upload de imagens. As imagen são redimensionadas para maxWidth x maxHeight.
 */
export default function ImageInput({
    name,
    label,
    imageField = "base64",
    multiple = false,
    geolocationField,
    maxWidth = 480,
    maxHeight = 480,
    quality = 0.7,
    format = "image/jpeg",
    small,
    onSelect,
    onRemove,
    sx,
    children,
    ...props
}) {
    const [coordinates] = useCoordinates();
    const [fileInput, setFileInput] = useState();
    const [deleteDialog, setDeleteDialog] = useState();
    const [dialogMobile, setDialogMobile] = useState();
    const { showError } = useNotification()

    let [files, setFiles] = useFieldValue(name);
    const mobile = window.cordova ? true : false
    const fileInputRef = useCallback((node) => {
        if (node) {
            setFileInput(node);
        }
    }, []);

    async function handleFileChangeMobile(dataImage) {
        console.log('dataImage',dataImage)    
        try {
            // Validação e limpeza dos dados base64
            if (!dataImage || typeof dataImage !== 'string') {
                showError("Dados da imagem inválidos!");
                return;
            }
            
            // Remove possíveis quebras de linha e espaços
            const cleanedDataImage = dataImage.replace(/\s/g, '');
            
            let src;
            
            // Verifica se é um caminho de arquivo (FILE_URI) ou base64 (DATA_URL)
            if (cleanedDataImage.startsWith('file://') || cleanedDataImage.startsWith('content://') || cleanedDataImage.startsWith('/')) {
                // É um caminho de arquivo - precisa converter para base64
                try {
                    // Usando cordova-plugin-file para ler o arquivo
                    const base64Data = await convertFileToBase64(cleanedDataImage);
                    src = "data:image/jpeg;base64," + base64Data;
                } catch (error) {
                    console.error("Erro ao converter arquivo para base64:", error);
                    showError("Erro ao processar arquivo de imagem!");
                    return;
                }
            } else {
                // Verifica se é um base64 válido
                if (!/^[A-Za-z0-9+/]*={0,2}$/.test(cleanedDataImage)) {
                    showError("Formato de imagem inválido!");
                    return;
                }
                src = "data:image/jpeg;base64," + cleanedDataImage;
            }
            
            console.log('Processando imagem mobile...');

            async function setImage(obj, image) {
                try {
                    _.set(obj, imageField, await scaleImage(image, maxWidth, maxHeight, format, quality));
                    
                    // Melhor tratamento da geolocalização
                    if (geolocationField) {
                        if (coordinates && coordinates.latitude && coordinates.longitude) {
                            _.set(obj, geolocationField, coordinates);
                            if (!obj.georeferencia) {
                                obj.georeferencia = coordinates;
                            }
                        } else {
                            console.warn("Coordenadas não disponíveis");
                            // Não bloqueia o processo se não houver geolocalização
                        }
                    }
                } catch (error) {
                    console.error("Erro ao processar imagem:", error);
                    showError("Erro ao processar a imagem!");
                    throw error;
                }
            }

        let newFiles = [];

        if (multiple) {
            const item = {};
            await setImage(item, src);
            newFiles.push(item);
            console.log('Imagem adicionada (múltipla):', newFiles.length);
            setFiles([...files, ...newFiles]);
        } else {
            newFiles = {
                ...files,
            };

            await setImage(newFiles, src);
            console.log('Imagem única processada');
            setFiles(newFiles);
        }

        if (dialogMobile) {
            dialogMobile.setOpen(false);
        }
        
        } catch (error) {
            console.error("Erro ao processar imagem mobile:", error);
            showError("Erro ao processar a imagem. Tente novamente.");
            
            if (dialogMobile) {
                dialogMobile.setOpen(false);
            }
        }
    }


    async function onFileSelected(event) {
        try {
            async function setImage(obj, image) {
                try {
                    _.set(obj, imageField, await scaleImage(image, maxWidth, maxHeight, format, quality));

                    // Melhor tratamento da geolocalização
                    if (geolocationField) {
                        if (coordinates && coordinates.latitude && coordinates.longitude) {
                            _.set(obj, geolocationField, coordinates);
                            if (!obj.georeferencia) {
                                obj.georeferencia = coordinates;
                            }
                        } else {
                            console.warn("Coordenadas não disponíveis para web");
                            // Não bloqueia o processo se não houver geolocalização
                        }
                    }
                } catch (error) {
                    console.error("Erro ao processar imagem web:", error);
                    showError("Erro ao processar a imagem!");
                    throw error;
                }
            }

            if (event.target.files.length > 0) {
                let newFiles = [];

                if (multiple) {
                    for (let file of event.target.files) {
                        console.log('Processando arquivo:', file.name);
                        const item = {};
                        await setImage(item, file);
                        newFiles.push(item);
                    }

                    setFiles([...files, ...newFiles]);
                } else {
                    newFiles = {
                        ...files,
                    };

                    await setImage(newFiles, event.target.files[0]);
                    console.log('Arquivo único processado:', event.target.files[0].name);
                    setFiles(newFiles);
                }

                if (onSelect) {
                    onSelect(newFiles);
                }
            }
        } catch (error) {
            console.error("Erro ao selecionar arquivos:", error);
            showError("Erro ao processar os arquivos selecionados. Tente novamente.");
        }
    }

    function selectFiles() {
        if (mobile) {
            dialogMobile.setOpen(true);
        } else {
            if (fileInput) {
                fileInput.click();
            }

        }
    }

    function remove(index) {
        showDialog(deleteDialog).then((confirm) => {
            if (confirm) {
                let newFiles;
                let removed;

                if (multiple) {
                    newFiles = [...files];
                    removed = newFiles.splice(index, 1)[0];
                } else {
                    removed = files;
                    newFiles = null;
                }

                setFiles(newFiles);

                if (onRemove) {
                    onRemove(removed, index);
                }
            }
        });
    }

    async function rotate(index, degrees) {
        let newFiles;

        if (multiple) {
            newFiles = [...files];
            newFiles[index][imageField] = await rotateImage(newFiles[index][imageField], degrees);
        } else {
            newFiles = { ...files };
            newFiles[imageField] = await rotateImage(newFiles[imageField], degrees);
        }

        setFiles(newFiles);
    }

    let fileArray = [];

    if (_.isArray(files)) {
        fileArray = files;
    } else if (files) {
        fileArray = [files];
    }

    return (
        <InputBox sx={sx}>
            <PField name={name}>
                {(fieldProps) => (
                    <>
                        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}>
                            {label ? <InputLabel>{label}</InputLabel> : null}

                            {/* Oculta botão adicionar em modo pequeno quando já houver uma imagem. */}
                            {small && !multiple && files ? null : (
                                <Box>
                                    <Button
                                        startIcon={small ? null : <AddIcon />}
                                        size={small ? "small" : "medium"}
                                        onClick={selectFiles}
                                        title="Adicionar Imagem"
                                    >
                                        {small ? <AddIcon /> : multiple ? "Adicionar Imagens" : "Selecionar Imagem"}
                                    </Button>

                                </Box>
                            )}

                            {fileArray.map((file, index) => (
                                <Box
                                    sx={{
                                        display: "flex",
                                        flexDirection: { xs: "column", sm: "row" },
                                        gap: 2,
                                        pb: multiple ? 2 : 0,
                                        borderBottom: "1px dotted",
                                        borderColor: "primary.main",
                                        "&:last-child": { borderBottom: 0 },
                                    }}
                                    key={index}
                                >
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: small ? 0.5 : 2 }}>
                                        {file[imageField] ? (
                                            <ImageBox
                                                sx={{ width: small ? null : { xs: "100%", sm: "200px" } }}
                                                maxWidth={small ? 120 : null}
                                                maxHeight={small ? 120 : null}
                                                src={file[imageField]}
                                                alt={file[imageField].name}
                                            />
                                        ) : (
                                            <Typography
                                                variant="caption"
                                                sx={{ width: { xs: "100%", sm: "200px", textAlign: "center" } }}
                                            >
                                                Imagem não carregada
                                            </Typography>
                                        )}

                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <ButtonGroup variant="outlined" size={small ? "small" : "medium"}>
                                                <Button onClick={() => rotate(index, -90)}>
                                                    <RotateLeftIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                                <Button onClick={() => remove(index)}>
                                                    <DeleteForeverIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                                <Button onClick={() => rotate(index, 90)}>
                                                    <RotateRightIcon fontSize={small ? "small" : "medium"} />
                                                </Button>
                                            </ButtonGroup>
                                        </Box>
                                    </Box>

                                    {geolocationField || children ? (
                                        <NestedField prefix={name} index={multiple ? index : null}>
                                            <FormContent>
                                                {geolocationField ? (
                                                    <GeolocationButton name={geolocationField} />
                                                ) : null}

                                                {children}
                                            </FormContent>
                                        </NestedField>
                                    ) : null}
                                </Box>
                            ))}
                        </Box>

                        <input
                            ref={fileInputRef}
                            hidden
                            type="file"
                            multiple={multiple}
                            onChange={(event) => onFileSelected(event)}
                        />
                    </>
                )}
            </PField>

            <ConfirmDialog title="Remover" setDialog={setDeleteDialog}>
                Deseja realmente remover esta foto?
            </ConfirmDialog>
            {/* <MenuDialog title={"IMG"} setDialog={setDialogMobile} menuItems={[{
                id: "galeria",
                label: "galeria",

            }, {
                id: "Camera",
                label: "Camera",

            }]} onClose={handleClose} /> */}

            <WindowDialog title={"Tipo de Imagem"} setDialog={setDialogMobile}
                contentProps={{ sx: { p: 2, flexDirection: "column", gap: 1 } }}
                notFullScreen {...props}>
                {(dialogProps) => {
                    return (
                        <>
                            <Button
                                onClick={() => {
                                    // Configuração otimizada para galeria
                                    const options = {
                                        quality: 50,
                                        destinationType: window.Camera ? window.Camera.DestinationType.FILE_URI : 1,
                                        sourceType: window.Camera ? window.Camera.PictureSourceType.SAVEDPHOTOALBUM : 0,
                                        encodingType: window.Camera ? window.Camera.EncodingType.JPEG : 0,
                                        mediaType: window.Camera ? window.Camera.MediaType.PICTURE : 0,
                                        correctOrientation: true
                                    };

                                    navigator.camera.getPicture(
                                        (imageData) => {
                                            console.log("Imagem da galeria recebida:", imageData.substring(0, 50) + "...");
                                            handleFileChangeMobile(imageData)
                                                .catch((error) => {
                                                    console.error("Erro ao processar imagem da galeria:", error);
                                                    showError("Erro ao processar imagem da galeria!");
                                                });
                                        },
                                        (err) => {
                                            console.error("Erro ao acessar galeria:", err);
                                            showError("Erro ao acessar a galeria. Verifique as permissões.");
                                        },
                                        options
                                    );
                                }}
                            >
                                Galeria
                            </Button>
                            <Button
                                onClick={() => {
                                    // Configuração otimizada para câmera
                                    const options = {
                                        quality: 50,
                                        destinationType: window.Camera ? window.Camera.DestinationType.FILE_URI : 1,
                                        sourceType: window.Camera ? window.Camera.PictureSourceType.CAMERA : 1,
                                        encodingType: window.Camera ? window.Camera.EncodingType.JPEG : 0,
                                        mediaType: window.Camera ? window.Camera.MediaType.PICTURE : 0,
                                        correctOrientation: true,
                                        saveToPhotoAlbum: true
                                    };

                                    navigator.camera.getPicture(
                                        (imageData) => {
                                            console.log("Imagem da câmera recebida:", imageData.substring(0, 50) + "...");
                                            handleFileChangeMobile(imageData)
                                                .catch((error) => {
                                                    console.error("Erro ao processar imagem da câmera:", error);
                                                    showError("Erro ao processar imagem da câmera!");
                                                });
                                        },
                                        (err) => {
                                            console.error("Erro ao acessar câmera:", err);
                                            showError("Erro ao acessar a câmera. Verifique as permissões.");
                                        },
                                        options
                                    );
                                }}
                            >
                                Câmera
                            </Button>
                        </>
                    )
                }}
            </WindowDialog>
        </InputBox>
    );
}
